import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Button,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import UserAuthentication from '../../../components/userAuthentication/index';
import AestheticProtocolModal from './aestheticProtocolModal';
import AestheticalProtocolProcedures from './aestheticalProtocolProcedures';
import IncomesModal from '../../../financial/incomes/incomesModal';
import Loading from '../../../components/loading';
import ErrorMessage from '../../../components/errorMessage';
import SuccessMessage from '../../../components/successMessage';
import { useAestheticProtocols } from './hooks/useAestheticProtocols';
import { useAestheticProtocolActions } from './hooks/useAestheticProtocolActions';

export default function AestheticProtocolWeb({ patientId }) {
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const {
    protocols,
    isLoading: isProtocolsLoading,
    error: listError,
    refetch: fetchProtocols,
  } = useAestheticProtocols(patientId);
  const {
    generateCharge: generateChargeRequest,
    start: startRequest,
    remove: removeRequest,
  } = useAestheticProtocolActions(patientId);

  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(listError);
    }
  }, [listError]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [protocolForActions, setProtocolForActions] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);

  const [openIncomeModal, setOpenIncomeModal] = useState(false);
  const [incomeDescription, setIncomeDescription] = useState('');
  const [incomeAmount, setIncomeAmount] = useState(0);
  const [incomeProfessionalId, setIncomeProfessionalId] = useState(null);

  const handleSave = () => {
    setIsModalOpen(false);
    fetchProtocols();
  };

  const handleRowClick = (protocol) => {
    setSelectedProtocol((prev) => (prev?.id === protocol.id ? null : protocol));
  };

  const handleMenuOpen = (e, protocol) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setProtocolForActions(protocol);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setProtocolForActions(null);
  };

  const handleDeleteClick = () => {
    setDeleteModal(true);
    setAnchorEl(null);
  };

  const generateCharge = () => {
    const protocol = protocolForActions;
    handleMenuClose();
    setLoading(true);
    generateChargeRequest(protocol.id)
      .then((returnFromApi) => {
        const charge = returnFromApi.data;
        setIncomeAmount(charge.amount);
        setIncomeDescription(charge.description);
        setIncomeProfessionalId(charge.professionalId);
        setLoading(false);
        setOpenIncomeModal(true);
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      });
  };

  const saveIncome = (returnFromApi) => {
    setShowSuccessMessage(true);
    setSuccessMessage(returnFromApi.data);
    setOpenIncomeModal(false);
  };

  const startProtocol = () => {
    const protocol = protocolForActions;
    handleMenuClose();
    setLoading(true);
    startRequest(protocol.id)
      .then((returnFromApi) => {
        setSuccessMessage(returnFromApi.data);
        setShowSuccessMessage(true);
        fetchProtocols();
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      })
      .finally(() => setLoading(false));
  };

  const handleConfirmDelete = () => {
    setLoading(true);
    removeRequest(protocolForActions.id)
      .then((returnFromApi) => {
        setSuccessMessage(returnFromApi.data);
        setShowSuccessMessage(true);
        setDeleteModal(false);
        if (selectedProtocol?.id === protocolForActions.id) setSelectedProtocol(null);
        setProtocolForActions(null);
        fetchProtocols();
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <UserAuthentication />
      <Loading isToHidden={isLoading === false && isProtocolsLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
        <Box
          sx={{
            flex: selectedProtocol ? '0 0 240px' : '1 1 auto',
            minWidth: 0,
            transition: 'flex 0.2s',
          }}
        >
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardHeader
              title={
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    Protocolos
                  </Typography>
                  {selectedProtocol && (
                    <Button
                      variant="outlined"
                      disableElevation
                      size="small"
                      onClick={() => setIsModalOpen(true)}
                      sx={{ mt: 1 }}
                    >
                      Adicionar Novo
                    </Button>
                  )}
                </Box>
              }
              action={
                !selectedProtocol && (
                  <Button variant="outlined" disableElevation onClick={() => setIsModalOpen(true)}>
                    Adicionar Novo
                  </Button>
                )
              }
            />
            <CardContent sx={{ p: selectedProtocol ? 1 : 2 }}>
              {protocols.length === 0 ? (
                <Typography align="center" sx={{ py: 4 }}>
                  Nenhum protocolo estético cadastrado ainda.
                </Typography>
              ) : selectedProtocol ? (
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      {protocols.map((protocol) => (
                        <TableRow
                          key={protocol.id}
                          hover
                          selected={selectedProtocol?.id === protocol.id}
                          onClick={() => handleRowClick(protocol)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight:
                                  selectedProtocol?.id === protocol.id ? 'bold' : 'normal',
                              }}
                            >
                              {protocol.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {protocol.professionalName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {protocol.totalAmountFormatted}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ p: 0 }}>
                            <IconButton size="small" onClick={(e) => handleMenuOpen(e, protocol)}>
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Protocolo</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Profissional</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Data de Início</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Valor</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {protocols.map((protocol) => (
                        <TableRow
                          key={protocol.id}
                          hover
                          onClick={() => handleRowClick(protocol)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{protocol.description}</TableCell>
                          <TableCell>{protocol.professionalName}</TableCell>
                          <TableCell>{protocol.initiatedAt}</TableCell>
                          <TableCell>{protocol.totalAmountFormatted}</TableCell>
                          <TableCell>
                            <Chip label={protocol.status} color="info" size="small" />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton onClick={(e) => handleMenuOpen(e, protocol)}>
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>

        {selectedProtocol && (
          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <AestheticalProtocolProcedures
              baseUrl={`/aesthetic-protocols/${selectedProtocol.id}/procedures`}
              subtitle={selectedProtocol.description}
              showCount={false}
            />
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 2 }}>
        <AestheticalProtocolProcedures
          baseUrl={patientId ? `/patients/${patientId}/aesthetic-avulsion-procedures` : null}
          applicationsResource="/aesthetic-avulsion-procedures"
          title="Procedimentos Avulsos"
          subtitle="Atendimento rápido sem vínculo com protocolo"
        />
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={startProtocol}>Iniciar Protocolo</MenuItem>
        <MenuItem onClick={generateCharge}>Gerar Cobrança</MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          Excluir
        </MenuItem>
      </Menu>

      <Dialog open={deleteModal} onClose={() => setDeleteModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir Protocolo</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Ao excluir o protocolo, todos os procedimentos e aplicações serão removidos
            permanentemente. Deseja continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModal(false)}>Cancelar</Button>
          <Button variant="contained" disableElevation color="error" onClick={handleConfirmDelete}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <AestheticProtocolModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        patientId={patientId}
      />

      {openIncomeModal && (
        <IncomesModal
          open
          onClose={() => setOpenIncomeModal(false)}
          onSaveSuccess={saveIncome}
          incomeId={null}
          incomeDescription={incomeDescription}
          incomeAmount={incomeAmount}
          incomeOrigin={'AESTHETIC_PROTOCOL'}
          patientId={patientId}
          professionalId={incomeProfessionalId}
        />
      )}
    </>
  );
}

AestheticProtocolWeb.propTypes = {
  patientId: PropTypes.string,
};

AestheticProtocolWeb.defaultProps = {
  patientId: null,
};
